import { useEffect, useState } from 'react'
import {
  Package,
  TrendingUp,
  Briefcase,
  ShieldCheck,
} from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useServerStore } from '../../shell/stores/serverStore'
import { useOperatorStore } from '../../stores/operatorStore'
import {
  dailyOrders,
  monthlyFinance,
  ORDER_STATUS_COLOR,
  ORDER_STATUS_LABEL,
  type OrderStatus,
} from './operationsData'
import { CommunicationGantt } from '../operator/CommunicationGantt'
import { OperatorProposalPanel } from '../operator/OperatorProposalPanel'

/**
 * Operations command center. Order and finance charts remain explicitly
 * marked demo data; the Communication Gantt is the live Operator projection.
 */
export default function OperationsDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [projectsReady, setProjectsReady] = useState(false)
  const activeServerId = useServerStore((state) => state.activeServerId)
  const projects = useOperatorStore((state) => state.projects)
  const proposals = useOperatorStore((state) => state.proposals)
  const proposalPackages = useOperatorStore(
    (state) => state.proposalPackages,
  )
  const proposalResults = useOperatorStore((state) => state.proposalResults)
  const projectsLoading = useOperatorStore((state) => state.loading.projects)
  const proposalsLoading = useOperatorStore(
    (state) => state.loading.proposals,
  )
  const projectsError = useOperatorStore((state) => state.errors.projects)
  const proposalsError = useOperatorStore((state) => state.errors.proposals)
  const packageError = useOperatorStore((state) => state.errors.package)
  const approvingProposalId = useOperatorStore(
    (state) => state.approvingProposalId,
  )
  const rejectingProposalId = useOperatorStore(
    (state) => state.rejectingProposalId,
  )
  const retryingActionId = useOperatorStore(
    (state) => state.retryingActionId,
  )
  const activateWorkspace = useOperatorStore((state) => state.activateWorkspace)
  const loadProjects = useOperatorStore((state) => state.loadProjects)
  const loadProposals = useOperatorStore((state) => state.loadProposals)
  const loadProposalPackage = useOperatorStore(
    (state) => state.loadProposalPackage,
  )
  const loadTimeline = useOperatorStore((state) => state.loadTimeline)
  const approveProposal = useOperatorStore((state) => state.approveProposal)
  const retryAction = useOperatorStore((state) => state.retryAction)
  const rejectProposal = useOperatorStore((state) => state.rejectProposal)
  const requestedProjectId = searchParams.get('project')
  const selectedProject = projectsReady
    ? projects.find(({ id }) => id === requestedProjectId) ?? projects[0] ?? null
    : null

  useEffect(() => {
    let active = true
    setProjectsReady(false)
    activateWorkspace(activeServerId)
    void loadProposals()
    void loadProjects().finally(() => {
      if (active) setProjectsReady(true)
    })
    return () => {
      active = false
    }
  }, [
    activeServerId,
    activateWorkspace,
    loadProjects,
    loadProposals,
  ])

  useEffect(() => {
    let active = true
    void (async () => {
      for (const proposal of proposals) {
        if (!active) return
        if (!proposalPackages[proposal.id]) {
          await loadProposalPackage(proposal.id)
        }
      }
    })()
    return () => {
      active = false
    }
  }, [loadProposalPackage, proposalPackages, proposals])

  useEffect(() => {
    if (!projectsReady) return
    const next = new URLSearchParams(searchParams)
    if (selectedProject) next.set('project', selectedProject.id)
    else next.delete('project')
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [projectsReady, searchParams, selectedProject, setSearchParams])

  const selectProject = (projectId: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('project', projectId)
    setSearchParams(next)
  }

  const approve = async (proposalId: string) => {
    const result = await approveProposal(proposalId)
    await loadProjects()
    if (selectedProject) await loadTimeline(selectedProject.id)
    return result
  }

  const retry = async (proposalId: string, actionId: string) => {
    const result = await retryAction(proposalId, actionId)
    if (!result) return null
    await Promise.all([
      loadProposals(),
      loadProposalPackage(proposalId),
      loadProjects(),
    ])
    if (selectedProject) await loadTimeline(selectedProject.id)
    return result
  }

  return (
    <div className="flex-1 min-w-0 h-full overflow-y-auto bg-background text-foreground">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b border-border/40 px-8 py-4 flex items-center gap-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-foreground">Operations</h1>
          <p className="text-xs text-muted-foreground">Evidence, delivery risk, and every waiting party in one control ledger</p>
        </div>
      </header>

      <main className="space-y-6 px-4 py-5 sm:px-6 lg:px-8 lg:py-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Block icon={<Package size={14} />} title="Orders by status" subtitle="Demo data · Last 30 days">
            <OrdersChart />
          </Block>
          <Block icon={<TrendingUp size={14} />} title="Revenue · Expenses · Profit" subtitle="Demo data · Last 6 months">
            <FinanceChart />
          </Block>
        </div>

        {(proposalsLoading || proposalsError || proposals.length > 0) && (
          <Block
            icon={<ShieldCheck size={14} />}
            title="Operator approvals"
            subtitle="Durable evidence and exact mutations"
          >
            {proposalsLoading && proposals.length === 0 ? (
              <div
                role="status"
                className="grid min-h-32 place-items-center text-xs text-muted-foreground"
              >
                Loading pending operator proposals...
              </div>
            ) : proposalsError ? (
              <p role="alert" className="text-xs text-destructive">
                {proposalsError}
              </p>
            ) : (
              <div className="grid gap-4">
                {proposals.map((proposal) => {
                  const reviewPackage = proposalPackages[proposal.id]
                  if (!reviewPackage) {
                    return packageError ? (
                      <p
                        key={proposal.id}
                        role="alert"
                        className="text-xs text-destructive"
                      >
                        {packageError}
                      </p>
                    ) : (
                      <div
                        key={proposal.id}
                        role="status"
                        className="grid min-h-32 place-items-center text-xs text-muted-foreground"
                      >
                        Loading reviewed operator package...
                      </div>
                    )
                  }
                  return (
                    <OperatorProposalPanel
                      key={proposal.id}
                      proposal={proposal}
                      reviewPackage={reviewPackage}
                      executionResult={proposalResults[proposal.id]}
                      onApprove={(proposalId) => approve(proposalId)}
                      onReject={rejectProposal}
                      onRetryAction={retry}
                      isApproving={approvingProposalId === proposal.id}
                      isRejecting={rejectingProposalId === proposal.id}
                      retryingActionId={retryingActionId}
                    />
                  )
                })}
              </div>
            )}
          </Block>
        )}

        <Block icon={<Briefcase size={14} />} title="Communication Gantt" subtitle="Live project projection · select any event for authorized evidence">
          {projects.length > 0 && (
            <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
              <label className="grid min-w-56 gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Operator project
                <select
                  aria-label="Operator project"
                  value={selectedProject?.id ?? ''}
                  onChange={(event) => selectProject(event.target.value)}
                  className="h-9 rounded-md border border-border bg-background px-3 text-xs normal-case tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}{project.client ? ` · ${project.client}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {selectedProject && (
                <span className="text-[10px] capitalize text-muted-foreground">
                  {selectedProject.health.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          )}
          {!projectsReady || projectsLoading ? (
            <div role="status" className="grid min-h-44 place-items-center rounded-lg border border-border bg-muted/20 text-xs text-muted-foreground">
              Loading live operator projects…
            </div>
          ) : projectsError ? (
            <div role="alert" className="grid min-h-44 place-items-center rounded-lg border border-destructive/30 bg-destructive/5 p-5 text-center text-xs text-destructive">
              {projectsError}
            </div>
          ) : selectedProject ? (
            <CommunicationGantt
              projectId={selectedProject.id}
              projectLabel={selectedProject.name}
            />
          ) : (
            <div className="grid min-h-44 place-items-center rounded-lg border border-dashed border-border bg-muted/15 p-5 text-center text-xs text-muted-foreground">
              No live operator projects are available in this workspace.
            </div>
          )}
        </Block>
      </main>
    </div>
  )
}

/* ─────────────────────────────── Building blocks ───────────────────── */

function Block({
  icon,
  title,
  subtitle,
  children,
}: { icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
      <header className="px-4 py-3 border-b border-border/40 flex items-center gap-2">
        <span className="text-foreground/70">{icon}</span>
        <span className="text-sm text-foreground font-semibold tracking-tight">{title}</span>
        {subtitle && <span className="text-[11px] text-muted-foreground/80 ml-1">· {subtitle}</span>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  )
}

/* ─────────────────────────────── Orders chart ──────────────────────── */

const STATUSES: OrderStatus[] = ['delivered', 'in_progress', 'created', 'cancelled', 'failed']

function OrdersChart() {
  const width = 700
  const height = 240
  const padding = { top: 10, right: 16, bottom: 28, left: 32 }
  const innerW = width - padding.left - padding.right
  const innerH = height - padding.top - padding.bottom
  const barWidth = innerW / dailyOrders.length

  const maxStack = Math.max(...dailyOrders.map((d) => Object.values(d.counts).reduce((a, b) => a + b, 0)))
  const yScale = (v: number) => innerH - (v / maxStack) * innerH

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(maxStack * t))

  return (
    <div className="overflow-x-auto">
      {/*
        text-foreground sets currentColor so the SVG axis labels and grid
        lines flip with the theme automatically — same node renders dark
        on light wallpaper and light on dark wallpaper.
      */}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-full text-foreground" style={{ minWidth: 600 }}>
        {/* Grid */}
        {yTicks.map((v) => (
          <g key={v}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={padding.top + yScale(v)}
              y2={padding.top + yScale(v)}
              stroke="currentColor"
              strokeOpacity={0.1}
              strokeDasharray="2 4"
            />
            <text
              x={padding.left - 6}
              y={padding.top + yScale(v) + 3}
              fill="currentColor"
              fillOpacity={0.5}
              fontSize={9}
              textAnchor="end"
            >
              {v}
            </text>
          </g>
        ))}

        {/* Stacked bars */}
        {dailyOrders.map((d, i) => {
          let cumulative = 0
          const x = padding.left + i * barWidth + 1
          const w = Math.max(barWidth - 2, 1)
          return (
            <g key={d.date}>
              {STATUSES.map((s) => {
                const value = d.counts[s]
                const y = padding.top + yScale(cumulative + value)
                const h = yScale(cumulative) - yScale(cumulative + value)
                cumulative += value
                return (
                  <rect
                    key={s}
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    fill={ORDER_STATUS_COLOR[s]}
                    fillOpacity={0.95}
                    rx={1}
                  >
                    <title>{`${d.date} · ${ORDER_STATUS_LABEL[s]}: ${value}`}</title>
                  </rect>
                )
              })}
            </g>
          )
        })}

        {/* X-axis: every 5th day label */}
        {dailyOrders.map((d, i) => {
          if (i % 5 !== 0 && i !== dailyOrders.length - 1) return null
          const x = padding.left + i * barWidth + barWidth / 2
          const day = new Date(d.date).getDate()
          return (
            <text
              key={d.date}
              x={x}
              y={height - padding.bottom + 14}
              fill="currentColor"
              fillOpacity={0.55}
              fontSize={9}
              textAnchor="middle"
            >
              {day}
            </text>
          )
        })}
      </svg>

      <Legend
        items={STATUSES.map((s) => ({ color: ORDER_STATUS_COLOR[s], label: ORDER_STATUS_LABEL[s], value: dailyOrders.reduce((sum, d) => sum + d.counts[s], 0) }))}
      />
    </div>
  )
}

function Legend({ items }: { items: { color: string; label: string; value?: number }[] }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px]">
      {items.map((it) => (
        <li key={it.label} className="flex items-center gap-1.5 text-foreground/70">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: it.color }} />
          <span>{it.label}</span>
          {typeof it.value === 'number' && (
            <span className="text-muted-foreground/80 tabular-nums">· {it.value.toLocaleString()}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

/* ─────────────────────────────── Finance chart ─────────────────────── */

function FinanceChart() {
  const width = 700
  const height = 240
  const padding = { top: 10, right: 16, bottom: 28, left: 50 }
  const innerW = width - padding.left - padding.right
  const innerH = height - padding.top - padding.bottom
  const data = monthlyFinance
  const maxV = Math.max(...data.map((d) => Math.max(d.revenue, d.expenses)))

  const xStep = innerW / (data.length - 1 || 1)
  const yScale = (v: number) => innerH - (v / maxV) * innerH

  const lineFor = (key: 'revenue' | 'expenses' | 'profit') =>
    data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${padding.left + i * xStep} ${padding.top + yScale(d[key])}`)
      .join(' ')

  const areaFor = (key: 'revenue' | 'expenses' | 'profit') => {
    const path = data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${padding.left + i * xStep} ${padding.top + yScale(d[key])}`)
      .join(' ')
    return `${path} L ${padding.left + (data.length - 1) * xStep} ${padding.top + innerH} L ${padding.left} ${padding.top + innerH} Z`
  }

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(maxV * t))

  const lastRow = data[data.length - 1]

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-full text-foreground" style={{ minWidth: 600 }}>
        <defs>
          <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="exp-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
            <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* Grid */}
        {yTicks.map((v) => (
          <g key={v}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={padding.top + yScale(v)}
              y2={padding.top + yScale(v)}
              stroke="currentColor"
              strokeOpacity={0.1}
              strokeDasharray="2 4"
            />
            <text
              x={padding.left - 6}
              y={padding.top + yScale(v) + 3}
              fill="currentColor"
              fillOpacity={0.5}
              fontSize={9}
              textAnchor="end"
            >
              {abbreviateMoney(v)}
            </text>
          </g>
        ))}

        {/* Areas */}
        <path d={areaFor('revenue')} fill="url(#rev-grad)" />
        <path d={areaFor('expenses')} fill="url(#exp-grad)" />

        {/* Lines */}
        <path d={lineFor('revenue')} stroke="#22c55e" strokeWidth={2} fill="none" />
        <path d={lineFor('expenses')} stroke="#ef4444" strokeWidth={2} fill="none" />
        <path d={lineFor('profit')} stroke="#a855f7" strokeWidth={2} fill="none" strokeDasharray="4 3" />

        {/* Dots */}
        {data.map((d, i) => (
          <g key={d.date}>
            {(['revenue', 'expenses', 'profit'] as const).map((k) => (
              <circle
                key={k}
                cx={padding.left + i * xStep}
                cy={padding.top + yScale(d[k])}
                r={3}
                fill={k === 'revenue' ? '#22c55e' : k === 'expenses' ? '#ef4444' : '#a855f7'}
              >
                <title>{`${d.date} · ${k}: ${formatMoney(d[k])}`}</title>
              </circle>
            ))}
          </g>
        ))}

        {/* X labels */}
        {data.map((d, i) => (
          <text
            key={d.date}
            x={padding.left + i * xStep}
            y={height - padding.bottom + 14}
            fill="currentColor"
            fillOpacity={0.55}
            fontSize={9}
            textAnchor="middle"
          >
            {new Date(d.date).toLocaleString(undefined, { month: 'short' })}
          </text>
        ))}
      </svg>

      <div className="grid grid-cols-3 gap-3 mt-3">
        <KpiPill label="Revenue" value={formatMoney(lastRow.revenue)} color="#22c55e" />
        <KpiPill label="Expenses" value={formatMoney(lastRow.expenses)} color="#ef4444" />
        <KpiPill label="Profit" value={formatMoney(lastRow.profit)} color="#a855f7" />
      </div>
    </div>
  )
}

function KpiPill({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-base font-semibold text-foreground tabular-nums">{value}</div>
    </div>
  )
}

function abbreviateMoney(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v}`
}

function formatMoney(v: number): string {
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}
